// App shell + tiny hash router (Capture / Booths). No framework — a switch over
// location.hash keeps the bundle small and the offline boot path trivial.

import { renderCapture } from './capture/captureScreen';
import { renderList } from './list/listScreen';
import { clearListState } from './list/listState';
import { renderSnapshot } from './snapshot/snapshotScreen';
import { renderVendors, renderVendorById } from './vendors/vendorsScreen';
import { renderDetail } from './detail/detailScreen';
import { renderProduct360 } from './product/productScreen';
import {
  renderShowDirectory,
  renderExhibitorDetail,
  renderHitListView,
} from './exhibitors/directoryScreen';
import { APP_VERSION } from './config';
import { getIdentity, isStubbed, onAuthChange, probeSession, signIn, signOut } from './auth/auth';
import { countUnsynced } from './db/booths';
import { syncNow, pullBooths, onSyncState, installSyncTriggers, type SyncResult } from './sync/syncEngine';
import { toast } from './ui/toast';

type Route =
  | 'capture'
  | 'list'
  | 'vendors'
  | 'vendor'
  | 'snapshot'
  | 'detail'
  | 'product'
  | 'show'
  | 'exhibitor'
  | 'hitlist';

const DETAIL_PREFIX = '#/booth/';
const PRODUCT_PREFIX = '#/product/';
const VENDOR_PREFIX = '#/vendor/';
const SHOW_PREFIX = '#/show/';
const EXHIBITOR_PREFIX = '#/exhibitor/';

function currentRoute(): Route {
  if (location.hash === '#/booths') return 'list';
  if (location.hash === '#/vendors') return 'vendors';
  if (location.hash === '#/data') return 'snapshot';
  if (location.hash === '#/hitlist') return 'hitlist';
  if (location.hash.startsWith(DETAIL_PREFIX)) return 'detail';
  if (location.hash.startsWith(PRODUCT_PREFIX)) return 'product';
  if (location.hash.startsWith(VENDOR_PREFIX)) return 'vendor';
  if (location.hash.startsWith(SHOW_PREFIX)) return 'show';
  if (location.hash.startsWith(EXHIBITOR_PREFIX)) return 'exhibitor';
  return 'capture';
}

/** Booth id for the detail route (else null). */
function currentBoothId(): string | null {
  return location.hash.startsWith(DETAIL_PREFIX)
    ? decodeURIComponent(location.hash.slice(DETAIL_PREFIX.length))
    : null;
}

/** Product SKU/grain for the product-detail route (else null). */
function currentProductId(): string | null {
  return location.hash.startsWith(PRODUCT_PREFIX)
    ? decodeURIComponent(location.hash.slice(PRODUCT_PREFIX.length))
    : null;
}

/** Vendor id for the vendor-360 route (else null). */
function currentVendorId(): string | null {
  return location.hash.startsWith(VENDOR_PREFIX)
    ? decodeURIComponent(location.hash.slice(VENDOR_PREFIX.length))
    : null;
}

/** Show slug for the exhibitor-directory route (else null). */
function currentShowKey(): string | null {
  return location.hash.startsWith(SHOW_PREFIX)
    ? decodeURIComponent(location.hash.slice(SHOW_PREFIX.length))
    : null;
}

/** Exhibitor key for the exhibitor-detail route (else null). */
function currentExhibitorKey(): string | null {
  return location.hash.startsWith(EXHIBITOR_PREFIX)
    ? decodeURIComponent(location.hash.slice(EXHIBITOR_PREFIX.length))
    : null;
}

export async function mountApp(root: HTMLElement): Promise<void> {
  root.innerHTML = `
    <header class="app-bar">
      <div class="app-bar__title">Booth Tracker <span class="app-bar__ver">v${APP_VERSION}</span></div>
      <div class="app-bar__actions">
        <button id="sync-btn" class="sync-btn" title="Sync now"><span class="sync-btn__glyph">⟳</span><em id="pending"></em></button>
        <div id="who" class="app-bar__who"></div>
      </div>
    </header>
    <main id="view" class="view"></main>
    <nav class="tabbar">
      <a href="#/capture" data-tab="capture" class="tab">＋<span>Capture</span></a>
      <a href="#/booths" data-tab="list" class="tab">≣<span>Booths <em id="count"></em></span></a>
      <a href="#/vendors" data-tab="vendors" class="tab">🔍<span>Lookup</span></a>
      <a href="#/data" data-tab="snapshot" class="tab">⚙<span>Info</span></a>
    </nav>
    <div id="offline-banner" class="offline-banner" hidden>Offline — capture still works</div>
  `;

  const view = root.querySelector<HTMLElement>('#view')!;
  const who = root.querySelector<HTMLElement>('#who')!;
  const syncBtn = root.querySelector<HTMLButtonElement>('#sync-btn')!;

  function renderWho(): void {
    const id = getIdentity();
    const tag = isStubbed() ? ' · stub' : '';
    // v1.2: an expired session must not keep showing the user's name as though
    // everything is fine — that was the whole complaint (header says "Lydia",
    // every authenticated call fails). Say so, and make saying so the fix.
    who.innerHTML = id.expired
      ? `<button id="who-btn" class="link app-bar__who--expired" title="Your sign-in expired — tap to sign back in">Session expired — Sign in</button>`
      : `<button id="who-btn" class="link">${id.name}${tag}</button>`;

    who.querySelector('#who-btn')!.addEventListener('click', async () => {
      try {
        // Expired signs back IN (it is not a signed-in session to sign out of).
        if (id.authenticated) await signOut();
        else await signIn();
      } catch (e) {
        console.warn('sign-in/out cancelled or failed', e);
      }
      renderWho();
    });
  }

  // The identity can now change with no user action behind it — a startup probe
  // resolving, or a background sync finding the token dead — so the header has to
  // follow it rather than being painted once at mount.
  const offAuth = onAuthChange(() => renderWho());
  window.addEventListener('beforeunload', offAuth);

  // v1.1: the Booths tab is an explicit "start fresh" — it drops the remembered
  // list position/filters so a tab entry always lands at the top, even when you
  // were just looking at a booth. (Coming back from a booth any other way — the
  // detail screen's ‹ Booths, or the browser Back button — resumes instead.)
  root.querySelector<HTMLAnchorElement>('.tab[data-tab="list"]')!.addEventListener('click', () => {
    clearListState();
  });

  // Which route we came FROM, so the list knows whether this is a return trip.
  let prevRoute: Route | null = null;

  async function renderRoute(): Promise<void> {
    const route = currentRoute();
    // Product detail and the standalone Vendor 360 both live under the Vendors
    // tab (reached from Product Lookup / a product's buy-side link), so keep it lit.
    // The v0.7 directory screens all hang off the Info tab's Shows section, so
    // they light Info — nobody loses track of which tab they came in through.
    const activeTab =
      route === 'product' || route === 'vendor'
        ? 'vendors'
        : route === 'show' || route === 'exhibitor' || route === 'hitlist'
          ? 'snapshot'
          : route;
    root.querySelectorAll<HTMLAnchorElement>('.tab').forEach((t) =>
      t.classList.toggle('tab--on', t.dataset.tab === activeTab),
    );
    if (route === 'list') {
      // Returning from a booth detail resumes (scroll + filters); anything else —
      // the Booths tab, a reload, a jump from another screen — starts at the top.
      await renderList(view, prevRoute === 'detail' ? 'restore' : 'fresh');
      // Pull remote booths on list open, then re-render if anything merged in —
      // don't block the first paint on the network. 'preserve': the user may have
      // scrolled or filtered in the meantime and a merge must not move them.
      void pullBooths().then(async (pulled) => {
        if (pulled > 0 && currentRoute() === 'list') {
          await renderList(view, 'preserve');
          await refreshCount();
        }
      });
    } else if (route === 'vendors') {
      await renderVendors(view);
    } else if (route === 'vendor') {
      const id = currentVendorId();
      if (id) {
        await renderVendorById(view, id, () => {
          location.hash = '#/vendors';
        });
      } else {
        location.hash = '#/vendors';
      }
    } else if (route === 'snapshot') {
      await renderSnapshot(view);
    } else if (route === 'show') {
      const key = currentShowKey();
      if (key) await renderShowDirectory(view, key);
      else location.hash = '#/data';
    } else if (route === 'exhibitor') {
      const key = currentExhibitorKey();
      if (key) await renderExhibitorDetail(view, key);
      else location.hash = '#/data';
    } else if (route === 'hitlist') {
      await renderHitListView(view);
    } else if (route === 'product') {
      const id = currentProductId();
      if (id) {
        await renderProduct360(view, id, () => {
          location.hash = '#/vendors';
        });
      } else {
        location.hash = '#/vendors';
      }
    } else if (route === 'detail') {
      const id = currentBoothId();
      if (id) {
        await renderDetail(view, id, () => {
          location.hash = '#/booths';
        });
      } else {
        location.hash = '#/booths';
      }
    } else {
      // v1.1: Save no longer navigates — the callback just refreshes the pending
      // badge, and the capture screen stays on the booth it just saved.
      renderCapture(view, () => {
        void refreshCount();
      });
    }
    prevRoute = route;
    await refreshCount();
  }

  async function refreshCount(): Promise<void> {
    const n = await countUnsynced();
    const countEl = root.querySelector<HTMLElement>('#count')!;
    const pendingEl = root.querySelector<HTMLElement>('#pending')!;
    countEl.textContent = n ? String(n) : '';
    pendingEl.textContent = n ? String(n) : '';
    syncBtn.classList.toggle('sync-btn--pending', n > 0);
  }

  function describeResult(r: SyncResult): string {
    if (r.skipped === 'offline') return 'Offline — will sync when online';
    if (r.skipped === 'signed-out') return 'Sign in to sync';
    if (r.skipped === 'busy') return 'Sync already running';
    if (r.attempted === 0 && r.pulled === 0) return 'Nothing to sync';
    if (r.failed === 0) {
      const pulled = r.pulled > 0 ? ` · ${r.pulled} pulled` : '';
      return `Synced ✓ ${r.synced}${pulled}`;
    }
    // Surface the real failure (HTTP status/body or token error) so a device user
    // can see WHY a booth stayed "on device" without a desktop console.
    const why = r.error ? ` — ${r.error}` : ' — will retry';
    return `Synced ${r.synced}, ${r.failed} failed${why}`;
  }

  // Manual sync.
  syncBtn.addEventListener('click', async () => {
    const r = await syncNow();
    toast(describeResult(r), r.failed > 0 || r.skipped === 'signed-out' ? 'warn' : 'ok');
  });

  // Reflect sync activity: spinner while running; refresh views when it settles.
  const offSync = onSyncState(async ({ running, result }) => {
    syncBtn.classList.toggle('sync-btn--spinning', running);
    if (!running) {
      await refreshCount();
      // 'preserve': a sync landing mid-scroll must not throw the user back to the top.
      if (currentRoute() === 'list') await renderList(view, 'preserve');
      if (result && result.synced > 0) toast(describeResult(result), 'ok');
    }
  });
  window.addEventListener('beforeunload', offSync);

  // Offline indicator (capture is unaffected; this is just honest feedback).
  const banner = root.querySelector<HTMLElement>('#offline-banner')!;
  const syncOnline = () => (banner.hidden = navigator.onLine);
  window.addEventListener('online', syncOnline);
  window.addEventListener('offline', syncOnline);
  syncOnline();

  // Session probe (v1.2). Startup + every return to connectivity, ONLINE ONLY:
  // offline cannot tell an expired refresh token from no signal, so we leave the
  // cached identity alone there and never nag. Fire-and-forget — nothing on screen
  // waits for it, and the header re-renders itself via onAuthChange.
  window.addEventListener('online', () => void probeSession());
  void probeSession();

  window.addEventListener('hashchange', () => void renderRoute());

  // Foreground sync triggers (online / focus / visibility / initial load).
  installSyncTriggers();

  renderWho();
  await renderRoute();
}
